import { Input, OnChanges, OnInit, SimpleChanges, Directive } from '@angular/core';
import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { Column } from '@miq/catalog';
import { MiqTouchable, MiqValidatable } from '@shared/behaviors';
import { isMasked } from '@shared/utils/helpers/sanitizers';

export interface FieldValidation {
  type: string;
  format: string;
  value: string;
}

@Directive()
export class BaseInput implements OnInit, OnChanges, MiqTouchable, MiqValidatable {
  @Input() disabled = false;
  @Input() required = false;
  @Input() currentValue: any;
  @Input() UOMValidation = false;
  @Input() warning = false;
  @Input() set valueSetter(vSetter: (data?: any) => any) {
    this._valueSetter = vSetter;
  }

  @Input() set column(column) {
    this._column = column;
    this._valueSetter = column.valueSetter;
  }

  get column() {
    return this._column;
  }

  get valueSetter() {
    return this._valueSetter;
  }

  public fc: UntypedFormControl;
  public _valueSetter: (data?: any) => any;
  public maxlength: number;

  public validSnapshot = true;

  public isMaskedValue = false;
  protected _column: Column;

  ngOnChanges({ disabled, required, currentValue }: SimpleChanges): void {
    if (disabled && !disabled.firstChange) {
      this.evaluateDisability();
    }

    if (required && !required.firstChange) {
      this.updateValidators();
    }

    if (currentValue && !currentValue.firstChange) {
      this.isMaskedValue = isMasked(this.currentValue);
      const isDisabled = this.disabled || this.isMaskedValue;
      if (this.fc != null) {
        // we need to update the disabled state when we change
        // the value
        if (isDisabled && this.fc.enabled) {
          this.fc.disable();
        } else if (!isDisabled && this.fc.disabled) {
          this.fc.enable();
        }
      }
    }
  }

  /**
   * Initializes isMaskedValue & fc attributes. form initialization is made
   * by calling initFormControl
   */
  ngOnInit(): void {
    this.isMaskedValue = isMasked(this.currentValue);
    this.initFormControl();
  }

  protected evaluateDisability() {
    if (this.disabled && this.fc) {
      this.fc.disable();
    } else {
      this.fc.enable();
    }
  }

  protected updateValidators() {
    this.fc.setValidators(this.controlValidators());
  }

  /**
   * It creates a new FormControl with formState object returned by controlValues
   * method and validators array by calling controlValidators. this allows child
   * components to adjust the logic of form control creation as their own need.
   * if no change required this process works as default
   */
  protected initFormControl() {
    this.fc = new UntypedFormControl(this.controlValues(), this.controlValidators());
  }

  /**
   * Returns an object state for FormControl that defines the initial value and
   * the disabled(Optional) state of the control while using the
   * `new FormControl(formState, validatorOpts?, asyncValidator)`
   */
  protected controlValues(): { value: any; disabled?: boolean } {
    return { value: this.currentValue, disabled: this.disabled || this.isMaskedValue };
  }

  protected controlValidators(): ValidatorFn[] {
    const extraValidations = [];
    if (this.column && this.column.options && this.column.options.validations) {
      extraValidations.push(...this.setValidations(this.column.options.validations));
    }
    if (this.required) {
      extraValidations.push(Validators.required);
    }
    return !this.isMaskedValue ? [...extraValidations] : [];
  }

  private setValidations(validationRaws: string[]): ValidatorFn[] {
    const result: ValidatorFn[] = [];
    validationRaws.forEach(validationRaw => {
      if (validationRaw.includes('maxLength')) {
        const convertedNumber = Number(validationRaw.split(':')[1]);
        if (!isNaN(convertedNumber) && Number.isInteger(convertedNumber)) {
          this.maxlength = convertedNumber;
          result.push(Validators.maxLength(convertedNumber));
        }
      }
    });
    return result;
  }

  isValid(): boolean {
    if (isMasked(this.fc.value)) {
      return true;
    }
    const valid =
      // form control is valid
      this.fc.valid ||
      // field NOT required AND either is untouched OR is disabled
      (!this.required && (this.fc.untouched || this.disabled)) ||
      // is disabled AND required AND value NOT empty
      (this.disabled && this.required && this.fc.value != null && this.fc.value !== '');

    if (!valid && (this.fc.pristine || this.fc.untouched)) {
      this.markAsTouched();
    }
    this.validSnapshot = valid;
    return valid;
  }

  isTouched() {
    return this.fc.dirty;
  }

  markAsTouched() {
    this.fc.markAsTouched();
  }
}
